"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Search, Store as StoreIcon, Tag, Flame, Package, DollarSign, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Error146 } from "@/components/error-146";

interface SearchResults {
  data: {
    stores: Array<{ id: string; slug: string; name: string; logo: string | null; cashbackRate: number | null }>;
    cashback: Array<{ id: string; slug: string; name: string; logo: string | null; cashbackRate: number | null }>;
    coupons: Array<{
      id: string;
      title: string;
      code: string | null;
      discount: number | null;
      discountType: string | null;
      store: { slug: string; name: string; logo: string | null } | null;
    }>;
    deals: Array<{
      id: string;
      title: string;
      type: string;
      price: number | null;
      imageUrl: string | null;
      images: string[];
      temperature: number;
    }>;
    products: Array<{
      id: string;
      name: string;
      brand: string | null;
      imageUrl: string | null;
      lowestPrice: number | null;
    }>;
  };
  meta?: {
    /** Buckets realmente disponibles: excluye los de funciones apagadas. */
    enabledBuckets?: string[];
    error146?: unknown;
  };
}

interface Props {
  autoFocus?: boolean;
  onClose?: () => void;
}

export function GlobalSearch({ autoFocus, onClose }: Props) {
  const router = useRouter();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SearchResults["data"] | null>(null);
  const [meta, setMeta] = useState<SearchResults["meta"] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const value = q.trim();
    if (value.length < 2) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setOpen(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(value)}&limit=5`, { signal: ctrl.signal });
        if (!res.ok) throw new Error("search failed");
        const json: SearchResults = await res.json();
        setData(json.data);
        setMeta(json.meta ?? null);
      } catch (e) {
        if ((e as Error).name !== "AbortError") setData(null);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [q]);

  function go(path: string) {
    setOpen(false);
    onClose?.();
    router.push(path);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const value = q.trim();
    if (!value) return;
    go(`/buscar?q=${encodeURIComponent(value)}`);
  }

  // El texto guía sólo nombra lo que la búsqueda realmente devuelve: el
  // endpoint omite los buckets de funciones apagadas, así que en cuanto llega
  // la primera respuesta el placeholder deja de prometer lo que no existe.
  const buckets = meta?.enabledBuckets;
  const placeholder = buckets
    ? `Buscar ${[
        buckets.includes("products") ? "productos" : null,
        buckets.includes("stores") ? "tiendas" : null,
        buckets.includes("deals") ? "ofertas" : null,
        buckets.includes("coupons") ? "cupones" : null,
        buckets.includes("cashback") ? "cashback" : null,
      ]
        .filter(Boolean)
        .join(", ")}...`
    : "Buscar productos y tiendas...";

  const hasResults =
    data &&
    (data.stores.length + data.cashback.length + data.coupons.length + data.deals.length + data.products.length > 0);

  return (
    <div ref={containerRef} className="relative">
      <form onSubmit={submit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => q.trim().length >= 2 && setOpen(true)}
            placeholder={placeholder}
            className="w-full h-10 pl-10 pr-10 rounded-lg border border-input bg-muted text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
          />
          {q ? (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setData(null);
                inputRef.current?.focus();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded hover:bg-muted-foreground/10"
              aria-label="Limpiar"
            >
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          ) : (
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground border rounded px-1">
              ESC
            </kbd>
          )}
        </div>
      </form>

      {open && q.trim().length >= 2 ? (
        <div className="absolute left-0 right-0 top-full mt-2 bg-background border rounded-xl shadow-xl z-50 max-h-[70vh] overflow-y-auto">
          {loading && !data ? (
            <div className="p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Buscando…
            </div>
          ) : meta?.error146 && !loading ? (
            <Error146 compact />
          ) : !hasResults ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              Sin resultados para <span className="font-medium">&ldquo;{q}&rdquo;</span>
            </div>
          ) : (
            <div className="divide-y">
              {data!.stores.length > 0 ? (
                <Section label="Tiendas" icon={StoreIcon}>
                  {data!.stores.map((s) => (
                    <Row
                      key={s.id}
                      onClick={() => go(`/tienda/${s.slug}`)}
                      leading={s.logo ? <img src={s.logo} alt="" className="w-8 h-8 object-contain rounded" /> : <StoreIcon className="w-5 h-5 text-muted-foreground" />}
                      title={s.name}
                      subtitle={s.cashbackRate ? `Cashback ${s.cashbackRate}%` : undefined}
                    />
                  ))}
                </Section>
              ) : null}

              {data!.cashback.length > 0 ? (
                <Section label="Cashback" icon={DollarSign}>
                  {data!.cashback.map((s) => (
                    <Row
                      key={s.id}
                      onClick={() => go(`/tienda/${s.slug}`)}
                      leading={s.logo ? <img src={s.logo} alt="" className="w-8 h-8 object-contain rounded" /> : <DollarSign className="w-5 h-5 text-emerald-600" />}
                      title={s.name}
                      subtitle={`hasta ${s.cashbackRate}% de cashback`}
                    />
                  ))}
                </Section>
              ) : null}

              {data!.coupons.length > 0 ? (
                <Section label="Cupones" icon={Tag}>
                  {data!.coupons.map((c) => (
                    <Row
                      key={c.id}
                      onClick={() => go(`/cupones?q=${encodeURIComponent(c.title)}`)}
                      leading={c.store?.logo ? <img src={c.store.logo} alt="" className="w-8 h-8 object-contain rounded" /> : <Tag className="w-5 h-5 text-emerald-600" />}
                      title={c.title}
                      subtitle={[c.store?.name, c.code ? `Código: ${c.code}` : c.discount ? `-${c.discount}${c.discountType === "AMOUNT" ? " $" : "%"}` : null].filter(Boolean).join(" · ")}
                    />
                  ))}
                </Section>
              ) : null}

              {data!.deals.length > 0 ? (
                <Section label="Ofertas" icon={Flame}>
                  {data!.deals.map((d) => {
                    const thumb = d.images?.[0] ?? d.imageUrl ?? null;
                    return (
                      <Row
                        key={d.id}
                        onClick={() => go(`/ofertas/${d.id}`)}
                        leading={thumb ? <img src={thumb} alt="" className="w-8 h-8 object-cover rounded" /> : <Flame className="w-5 h-5 text-orange-500" />}
                        title={d.title}
                        subtitle={[
                          d.temperature ? `${d.temperature}°` : null,
                          d.price ? `$${d.price.toLocaleString("es-MX")}` : null,
                        ].filter(Boolean).join(" · ")}
                      />
                    );
                  })}
                </Section>
              ) : null}

              {data!.products.length > 0 ? (
                <Section label="Productos" icon={Package}>
                  {data!.products.map((p) => (
                    <Row
                      key={p.id}
                      onClick={() => go(`/comparador/${p.id}`)}
                      leading={p.imageUrl ? <img src={p.imageUrl} alt="" className="w-8 h-8 object-contain rounded" /> : <Package className="w-5 h-5 text-muted-foreground" />}
                      title={p.name}
                      subtitle={[p.brand, p.lowestPrice ? `desde $${p.lowestPrice.toLocaleString("es-MX")}` : null].filter(Boolean).join(" · ")}
                    />
                  ))}
                </Section>
              ) : null}

              <Link
                href={`/buscar?q=${encodeURIComponent(q.trim())}`}
                className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-primary hover:bg-muted"
                onClick={() => {
                  setOpen(false);
                  onClose?.();
                }}
              >
                Ver todos los resultados para &ldquo;{q}&rdquo;
              </Link>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Section({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: typeof StoreIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="py-1">
      <div className="px-4 py-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({
  onClick,
  leading,
  title,
  subtitle,
}: {
  onClick: () => void;
  leading?: React.ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("w-full flex items-center gap-3 px-4 py-2 hover:bg-muted text-left")}
    >
      <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-muted rounded">{leading}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{title}</p>
        {subtitle ? <p className="text-xs text-muted-foreground truncate">{subtitle}</p> : null}
      </div>
    </button>
  );
}
